import * as THREE from "three";

export class BlockManager {
  constructor(scene) {
    this.scene = scene;
    this.blocks = [];

    // Add spatial partitioning for more efficient block lookup
    this.blockGrid = {};
    this.gridCellSize = 5; // Size of each grid cell in world units

    // Block dimensions
    this.blockWidth = 4; // 400px at 1000px effective height
    this.blockHeight = 1; // 100px at 1000px effective height

    // Base ground level - bottom of the screen
    this.baseGroundY = -5.0; // Base level for ground blocks (lowered by 0.5 units)

    // Shared geometry + material reused by every block. The geometry is a unit
    // box; per-block size is applied via mesh.scale, so we allocate one geometry
    // and one material instead of one per block (was ~hundreds of live
    // geometries). Collision is unaffected — it uses the precomputed `bounds`
    // records, not mesh geometry. Disposed once in dispose(); same pattern as
    // ProjectileManager.
    this.sharedGeometry = new THREE.BoxGeometry(1, 1, 0.1);
    this.sharedMaterial = new THREE.MeshStandardMaterial({
      color: 0x000000,
      emissive: 0x000000,
      emissiveIntensity: 1.0,
    });
  }

  // Helper method to get grid cell key for a position
  getGridKey(x) {
    return Math.floor(x / this.gridCellSize);
  }

  // Add block to spatial grid
  addBlockToGrid(block) {
    // Calculate the grid cells this block spans
    const startCell = this.getGridKey(block.bounds.left);
    const endCell = this.getGridKey(block.bounds.right);

    // Add the block to all cells it overlaps
    for (let cell = startCell; cell <= endCell; cell++) {
      if (!this.blockGrid[cell]) {
        this.blockGrid[cell] = [];
      }
      this.blockGrid[cell].push(block);
    }
  }

  // Remove block from spatial grid
  removeBlockFromGrid(block) {
    const startCell = this.getGridKey(block.bounds.left);
    const endCell = this.getGridKey(block.bounds.right);

    for (let cell = startCell; cell <= endCell; cell++) {
      if (this.blockGrid[cell]) {
        const index = this.blockGrid[cell].indexOf(block);
        if (index !== -1) {
          this.blockGrid[cell].splice(index, 1);
        }
        // Clean up empty cells
        if (this.blockGrid[cell].length === 0) {
          delete this.blockGrid[cell];
        }
      }
    }
  }

  createBlock(x, y, width, height) {
    const block = new THREE.Mesh(this.sharedGeometry, this.sharedMaterial);

    // Position the block at its center and apply its size via scale, since the
    // shared geometry is a unit box.
    block.position.set(x + width / 2, y + height / 2, 0);
    block.scale.set(width, height, 1);
    block.castShadow = true;
    block.receiveShadow = false;

    this.scene.add(block);

    // Store the block with its collision data
    const blockData = {
      mesh: block,
      x: x,
      y: y,
      width: width,
      height: height,
      // Store bounds for collision detection
      bounds: {
        left: x,
        right: x + width,
        top: y + height,
        bottom: y,
      },
    };

    this.blocks.push(blockData);

    // Add to spatial grid for efficient lookup
    this.addBlockToGrid(blockData);

    return blockData;
  }

  // Get all blocks that could possibly collide with an object at position x
  getBlocksNearX(x, radius = 3) {
    // Use our spatial grid to efficiently find nearby blocks
    const result = new Set();

    // Calculate grid cells to check
    const startCell = this.getGridKey(x - radius);
    const endCell = this.getGridKey(x + radius);

    // Collect blocks from all relevant grid cells
    for (let cell = startCell; cell <= endCell; cell++) {
      if (this.blockGrid[cell]) {
        for (const block of this.blockGrid[cell]) {
          result.add(block);
        }
      }
    }

    return Array.from(result);
  }

  // Check if a point is inside any block
  isPointInsideAnyBlock(x, y) {
    for (const block of this.blocks) {
      if (
        x >= block.bounds.left &&
        x <= block.bounds.right &&
        y >= block.bounds.bottom &&
        y <= block.bounds.top
      ) {
        return true;
      }
    }
    return false;
  }

  // Find the top of the highest block under a given position
  findGroundYAtPosition(x, playerBottom) {
    let highestY = null;
    let closestYBelowPlayer = null;
    let smallestYDifference = Infinity;

    // Get nearby blocks for efficiency
    const nearbyBlocks = this.getBlocksNearX(x);

    for (const block of nearbyBlocks) {
      // Check if the x position is within the block's horizontal bounds
      if (x >= block.bounds.left && x <= block.bounds.right) {
        // Track the closest block top below the player for precise landing
        if (block.bounds.top <= playerBottom) {
          const difference = playerBottom - block.bounds.top;
          if (difference < smallestYDifference) {
            smallestYDifference = difference;
            closestYBelowPlayer = block.bounds.top;
          }
        }
      }
    }

    // Only return ground below the player, not any ground
    return closestYBelowPlayer;
  }

  // Check for horizontal collision with blocks
  checkHorizontalCollision(playerBounds) {
    // Get nearby blocks for efficiency - use only blocks ahead of player
    const playerCenterX =
      playerBounds.left + (playerBounds.right - playerBounds.left) / 2;
    // Use a smaller radius for optimization
    const nearbyBlocks = this.getBlocksNearX(playerCenterX, 5);

    // Filter blocks to only include those that could potentially be collided with
    // This prevents false collisions when walking off blocks (blocks below the player)
    const blocksAhead = nearbyBlocks.filter(
      (block) =>
        // Keep blocks where the player's right edge is ahead of the block's left edge
        // and the block's left edge is ahead of the player's left edge
        // This ensures we only consider blocks that are partially or fully ahead of the player
        playerBounds.right >= block.bounds.left &&
        block.bounds.left >= playerBounds.left
    );

    // Sort blocks by leftmost edge for early termination
    // This ensures we find the closest block first
    blocksAhead.sort((a, b) => a.bounds.left - b.bounds.left);

    for (const block of blocksAhead) {
      // Now check vertical overlap with no buffer
      if (
        playerBounds.top > block.bounds.bottom &&
        playerBounds.bottom < block.bounds.top
      ) {
        // We have a collision - resolve by positioning player to the left of the block
        return {
          collision: true,
          blockX: block.bounds.left,
        };
      }
    }

    return { collision: false };
  }

  // Check for horizontal collision with blocks BEHIND the player (moving left).
  // Mirror of checkHorizontalCollision: the player normally only moves right, but a
  // backward wall jump moves them left, so we need to detect walls behind them.
  // Resolves to the block's RIGHT edge (player should sit at blockRight + width/2).
  checkHorizontalCollisionLeft(playerBounds) {
    const playerCenterX =
      playerBounds.left + (playerBounds.right - playerBounds.left) / 2;
    const nearbyBlocks = this.getBlocksNearX(playerCenterX, 5);

    // Only consider blocks behind the player: the block's right edge is behind the
    // player's right edge, and the player's left edge is behind the block's right edge.
    const blocksBehind = nearbyBlocks.filter(
      (block) =>
        playerBounds.left <= block.bounds.right &&
        block.bounds.right <= playerBounds.right
    );

    // Sort by rightmost edge descending so we resolve against the closest block first.
    blocksBehind.sort((a, b) => b.bounds.right - a.bounds.right);

    for (const block of blocksBehind) {
      // Same vertical-overlap test as the rightward check.
      if (
        playerBounds.top > block.bounds.bottom &&
        playerBounds.bottom < block.bounds.top
      ) {
        return {
          collision: true,
          blockRight: block.bounds.right,
        };
      }
    }

    return { collision: false };
  }

  // Check for vertical collision with blocks (for precise ceiling collisions)
  checkVerticalCollision(playerBounds, velocity = null) {
    // Get nearby blocks for efficiency
    const nearbyBlocks = this.getBlocksNearX(
      playerBounds.left + (playerBounds.right - playerBounds.left) / 2
    );

    for (const block of nearbyBlocks) {
      // Check if the horizontal positions overlap - with a small buffer for more precise collision
      if (
        playerBounds.right > block.bounds.left + 0.05 &&
        playerBounds.left < block.bounds.right - 0.05
      ) {
        // Check for collision with the bottom of the block (player hitting ceiling)
        // Only detect ceiling collisions when the player is moving upward (velocity > 0)
        if (
          playerBounds.top >= block.bounds.bottom &&
          playerBounds.top < block.bounds.top &&
          // Add buffer to make ceiling detection more accurate
          playerBounds.top > block.bounds.bottom + 0.01 &&
          // Only check ceiling collision when moving upward or when velocity is not provided
          (velocity === null || velocity > 0)
        ) {
          return {
            collision: true,
            direction: "up",
            blockY: block.bounds.bottom,
          };
        }

        // Check for collision with the top of the block (player landing)
        if (
          playerBounds.bottom <= block.bounds.top &&
          playerBounds.bottom > block.bounds.bottom
        ) {
          return {
            collision: true,
            direction: "down",
            blockY: block.bounds.top,
          };
        }
      }
    }

    return { collision: false };
  }

  // Check for collisions between blood particles and blocks
  checkBlockCollision(particleBounds) {
    const centerGridX = this.getGridKey(particleBounds.x);
    let collidingBlock = null;

    // Check the cell the particle is currently in, and adjacent cells
    for (let cellX = centerGridX - 1; cellX <= centerGridX + 1; cellX++) {
      const gridCell = this.blockGrid[cellX];
      if (gridCell) {
        for (const block of gridCell) {
          // Ensure block is not external (e.g., spawners)
          if (block.isExternal) {
            continue;
          }
          const blockBounds = block.bounds;

          // Simple AABB check for particle center vs block bounds
          if (
            particleBounds.x > blockBounds.left &&
            particleBounds.x < blockBounds.right &&
            particleBounds.y > blockBounds.bottom &&
            particleBounds.y < blockBounds.top
          ) {
            collidingBlock = block;
            break; // Found collision, no need to check further in this cell
          }
        }
      }
      if (collidingBlock) break; // Found collision, stop checking cells
    }

    // Return the colliding block object directly, or null if no collision
    return collidingBlock;
  }

  // Internal helper to remove a block's mesh from the scene. Geometry and
  // material are shared across all blocks, so they are NOT disposed here — only
  // once in dispose() at teardown.
  disposeBlock(blockData) {
    if (blockData && blockData.mesh) {
      this.scene.remove(blockData.mesh);
      blockData.mesh = null; // Clear reference
    }
  }

  // Remove a specific block and clean up its resources
  removeBlock(block) {
    if (!block) return;

    // Remove from spatial grid
    this.removeBlockFromGrid(block);

    // Remove from main blocks array
    const index = this.blocks.indexOf(block);
    if (index !== -1) {
      this.blocks.splice(index, 1);
    }

    // Dispose of THREE resources
    this.disposeBlock(block);
  }

  // Remove all blocks that are fully to the left of a given x-coordinate
  removeBlocksBeforeX(x) {
    // Iterate backwards to safely remove elements
    for (let i = this.blocks.length - 1; i >= 0; i--) {
      const block = this.blocks[i];
      // Remove if the block's right edge is less than the target x
      if (block.bounds.right < x) {
        this.removeBlock(block); // Use the new removeBlock function
      }
    }

    // Clean up empty grid cells (optional, but good practice)
    // This is less critical now that removeBlock handles grid cleanup
    // for (const key in this.blockGrid) {
    //   if (this.blockGrid[key].length === 0) {
    //     delete this.blockGrid[key];
    //   }
    // }
  }

  // Register an external block (like a spawner) for collision purposes
  registerExternalBlock(x, y, width, height) {
    // Create a block data object without a mesh
    const blockData = {
      x: x,
      y: y,
      width: width,
      height: height,
      // Store bounds for collision detection
      bounds: {
        left: x,
        right: x + width,
        top: y + height,
        bottom: y,
      },
      isExternal: true, // Mark as external for special handling
    };

    this.blocks.push(blockData);

    // Add to spatial grid for efficient lookup
    this.addBlockToGrid(blockData);

    return blockData;
  }

  // Unregister an external block
  unregisterExternalBlock(blockData) {
    const index = this.blocks.indexOf(blockData);
    if (index !== -1) {
      // Remove from spatial grid first
      this.removeBlockFromGrid(blockData);

      // Remove from blocks array
      this.blocks.splice(index, 1);
    }
  }

  // Get all blocks (useful for ProjectileManager collision checks)
  getBlocks() {
    return this.blocks;
  }

  reset() {
    // Dispose all blocks properly before clearing arrays
    for (let i = this.blocks.length - 1; i >= 0; i--) {
      // Only dispose non-external blocks managed by BlockManager
      if (!this.blocks[i].isExternal) {
        this.disposeBlock(this.blocks[i]);
      }
    }

    // Clear arrays and grid
    this.blocks = [];
    this.blockGrid = {};
    // Shared geometry/material are intentionally kept — they are reused across
    // restarts and freed only in dispose().
  }

  // Dispose shared GPU resources. Call once when the manager is torn down.
  dispose() {
    this.reset();
    this.sharedGeometry.dispose();
    this.sharedMaterial.dispose();
  }
}
